import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

// ============== Customer-facing progress block on invoice page ==============
export const BillingProgress = ({ quoteId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingOff, setPayingOff] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/quotes/${quoteId}/billing-progress?token=${token}`);
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (quoteId && token) load(); }, [quoteId, token]); // eslint-disable-line

  const payOff = async () => {
    if (!window.confirm("Pay off your full remaining balance now?")) return;
    setPayingOff(true);
    try {
      await axios.post(`${API}/public/quotes/${quoteId}/pay-off-now?token=${token}`);
      toast.success("Balance paid in full — thank you!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Charge failed. Please update your card.");
    } finally { setPayingOff(false); }
  };

  if (loading || !data) return null;
  if (!data.billing_schedule || data.billing_schedule === "full" || data.billing_schedule === "deposit_flexible") return null;
  if (!data.charges || data.charges.length === 0) return null;

  const pct = data.total_cents > 0 ? Math.min(100, Math.round((data.paid_cents / data.total_cents) * 100)) : 0;

  return (
    <div className="px-8 pb-6" data-testid="billing-progress">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">Payment Plan</h3>
          <span className="text-xs text-slate-900">
            {data.completed_count} of {data.total_count} {data.charges[0]?.kind === "installment" ? "installments" : "visits"} paid
          </span>
        </div>
        <div className="h-2 rounded-full bg-blue-100 overflow-hidden mb-2">
          <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between text-sm mb-4">
          <span className="text-slate-900">{fmt(data.paid_cents)} paid</span>
          <span className="text-gray-600">of {fmt(data.total_cents)}</span>
        </div>
        <div className="space-y-1.5">
          {data.charges.slice(0, 6).map((c, i) => (
            <div key={c.id || i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${c.status === "paid" ? "bg-blue-600" : c.status === "link_sent" ? "bg-amber-500" : "bg-gray-300"}`} />
                <span className="text-gray-700 truncate">
                  {c.kind === "installment" ? `Installment ${c.sequence} of ${c.total_count}` : `Visit ${c.sequence} of ${c.total_count}`}
                  <span className="text-gray-400"> · {fmtDate(c.due_date)}</span>
                </span>
              </div>
              <span className="font-medium text-gray-900 shrink-0 ml-2">
                {fmt(c.amount_cents)}
                {c.status === "paid" && <span className="text-blue-700 ml-1">✓</span>}
                {c.status === "link_sent" && <span className="text-amber-600 ml-1">!</span>}
              </span>
            </div>
          ))}
          {data.charges.length > 6 && (
            <div className="text-xs text-gray-500 italic">+{data.charges.length - 6} more…</div>
          )}
        </div>
        {data.remaining_cents > 0 && (
          <Button className="w-full mt-4 bg-blue-700 hover:bg-blue-800" onClick={payOff} disabled={payingOff} data-testid="pay-off-now-btn">
            {payingOff ? "Processing…" : `Pay remaining ${fmt(data.remaining_cents)} now`}
          </Button>
        )}
        <p className="text-[11px] text-gray-500 mt-3 italic">No cancellations, no refunds — reschedules only. If your card can't be charged, we'll email you a one-tap payment link.</p>
      </div>
    </div>
  );
};

// ============== Owner-facing upcoming charges card (Dashboard) ==============
export const UpcomingChargesCard = ({ slug }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tenants/${slug}/scheduled-charges?limit=50`, { headers: authHeaders() })
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return null;

  const upcoming = items.filter(i => i.status === "pending" || i.status === "link_sent").slice(0, 10);
  const recentPaid = items.filter(i => i.status === "paid").sort((a, b) => (b.paid_at || "").localeCompare(a.paid_at || "")).slice(0, 5);
  const failed = items.filter(i => i.status === "link_sent");

  if (upcoming.length === 0 && recentPaid.length === 0 && failed.length === 0) return null;

  return (
    <Card data-testid="upcoming-charges-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">💳 Scheduled Charges</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {failed.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-medium text-amber-900 mb-1">⚠️ {failed.length} charge{failed.length !== 1 ? "s" : ""} couldn't process</div>
            <p className="text-xs text-amber-800">Customer was sent a payment link. Will auto-retry in 3 days.</p>
          </div>
        )}
        {upcoming.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-700 mb-2">Upcoming (next {upcoming.length})</div>
            <div className="space-y-1.5">
              {upcoming.map(c => (
                <div key={c.id} className="flex items-center justify-between text-sm py-1">
                  <div className="min-w-0">
                    <span className="text-gray-900 truncate">
                      {c.kind === "installment" ? `Installment ${c.sequence}/${c.total_count}` : `Visit ${c.sequence}/${c.total_count}`}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">{fmtDate(c.due_date)}</span>
                  </div>
                  <span className="font-medium text-gray-900 shrink-0 ml-2">{fmt(c.amount_cents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {recentPaid.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-700 mb-2">Recently collected</div>
            <div className="space-y-1.5">
              {recentPaid.map(c => (
                <div key={c.id} className="flex items-center justify-between text-sm py-1">
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-600" />
                    <span className="text-gray-700 truncate">
                      {c.kind === "installment" ? `Installment ${c.sequence}/${c.total_count}` : `Visit ${c.sequence}/${c.total_count}`}
                    </span>
                  </div>
                  <span className="font-medium text-blue-800 shrink-0 ml-2">{fmt(c.amount_cents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Mini progress bar for lead cards ==============
export const LeadBillingMini = ({ quoteId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!quoteId) return;
    // We hit the authed scheduled-charges list then filter by quote_id. Simpler: use public endpoint — but it needs token. For the owner view we derive from quotes list data instead.
    // Best path: fetch charges filtered client-side.
    axios.get(`${API}/tenants/${localStorage.getItem("tenant_slug") || ""}/scheduled-charges?limit=500`, { headers: authHeaders() })
      .then(r => {
        const mine = (r.data || []).filter(c => c.quote_id === quoteId);
        if (mine.length === 0) { setData(null); return; }
        const total = mine.reduce((s, c) => s + (c.amount_cents || 0), 0);
        const paid = mine.filter(c => c.status === "paid").reduce((s, c) => s + (c.amount_cents || 0), 0);
        const kind = mine[0]?.kind;
        const completed = mine.filter(c => c.status === "paid").length;
        setData({ total, paid, kind, completed, count: mine.length });
      })
      .catch(() => setData(null));
  }, [quoteId]);

  if (!data || data.total === 0) return null;
  const pct = Math.min(100, Math.round((data.paid / data.total) * 100));
  return (
    <div className="mt-1" data-testid="lead-billing-mini">
      <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full bg-blue-800" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">
        {fmt(data.paid)} of {fmt(data.total)} · {data.completed}/{data.count} {data.kind === "installment" ? "installments" : "visits"} paid
      </div>
    </div>
  );
};
